import React, { useState, useEffect  } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Grid,
  Skeleton,
  Checkbox,
  ListItemText
} from "@mui/material";
import { AgChartsReact } from "ag-charts-react";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import ButtonComponent from "../../../Components/Common/button/Button";
import { saveAs } from "file-saver";
import ApiService from "../../../utils/fetch";
import { useSelector } from "react-redux";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DataTable from '../../../Components/Common/datatable/DataTable';
import { styled } from '@mui/material/styles';
import PerformanceGraph from "./PerformanceGraph";
import PlantTable from "./plantTable";
import BottlerTable from "./BottlerTable";
import CustomerCountryTable from "./CustomerCountryTable";

const monthLabels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];


  // Add aggregateLabels and termLabels for filter options
const aggregateLabels = {
  bottler: "Group",// Renamed from Bottler to Group
  customer : "Customer",
  country: "Country",
  "customer_id,location_id": "Customer - Location",
  location: "Location",
};

const termLabels = {
  "1": "Short Term",
  "2": "Long Term",
}

// Custom pill container for sliding highlight (same as ForecastTracker)
const PillContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  borderRadius: 20,
  background: '#f5f5f5',
  height: 32,
  minWidth: 180,
  alignItems: 'center',
  boxShadow: '0 0 0 1px #ddd',
}));

const PillHighlight = styled('div')(({ selected }) => ({
  position: 'absolute',
  top: 2,
  left: selected === '1' ? 2 : '50%', // <-- Fix: use '1'/'2' for term pill
  width: 'calc(50% - 4px)',
  height: 'calc(100% - 4px)',
  background: '#222',
  borderRadius: 16,
  transition: 'left 0.25s cubic-bezier(.4,0,.2,1)',
  zIndex: 1,
}));

const PillLabel = styled('div')(({ active }) => ({
  position: 'relative',
  zIndex: 2,
  flex: 1,
  textAlign: 'center',
  fontSize: '0.85rem',
  fontWeight: active ? 500 : 400,
  color: active ? '#fff' : '#222',
  cursor: 'pointer',
  padding: '0 8px',
  userSelect: 'none',
  transition: 'color 0.2s',
  lineHeight: '28px',
}));

// Pill styles for aggregate (Plant/Bottler) - match ErrorPerformance
const AggregatePillContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  borderRadius: 20,
  background: '#f5f5f5',
  height: 32,
  minWidth: 120,
  alignItems: 'center',
  boxShadow: '0 0 0 1px #ddd',
  marginLeft: 12,
}));

const AggregatePillHighlight = styled('div')(({ selected }) => ({
  position: 'absolute',
  top: 2,
  left: selected === 'plant' ? 2 : '50%',
  width: 'calc(50% - 4px)',
  height: 'calc(100% - 4px)',
  background: '#222',
  borderRadius: 16,
  transition: 'left 0.25s cubic-bezier(.4,0,.2,1)',
  zIndex: 1,
}));

const AggregatePillLabel = styled('div')(({ active }) => ({
  position: 'relative',
  zIndex: 2,
  flex: 1,
  textAlign: 'center',
  fontSize: '0.85rem',
  fontWeight: active ? 500 : 400,
  color: active ? '#fff' : '#222',
  cursor: 'pointer',
  padding: '0 8px',
  userSelect: 'none',
  transition: 'color 0.2s',
  lineHeight: '28px',
}));

const DashboardCharts = ({ aggregate }) => {
  const [monthList, setMonthList] = useState([]);
  const [monthListLoading, setMonthListLoading] = useState(true);
  const [monthCount, setMonthCount] = useState(9);
  const [selectedMonths, setSelectedMonths] = useState([]);
  // Set default agg from aggregate prop, fallback to 'plant'
  const [agg, setAgg] = useState(aggregate || "plant");
  const [term, setTerm] = useState("1"); // "1" for Short Term, "2" for Long Term
  const [forecastPerformance, setForecastPerformance] = useState([]);
  const [forecastPerformanceLoading, setForecastPerformanceLoading] = useState(false);

  const finalTreeData = useSelector((state) => state.entity.finalTreeData);

  const handleAggregateChange = (e) => setAgg(e.target.value);
  // Handler for pill filter
  const handleTermChange = (newTerm) => {
    setTerm(newTerm);
  };

  // Store the initial two months from API for table components
  const [tableMonths, setTableMonths] = useState([]);

  // Fetch N months from API for dropdown
  useEffect(() => {
    const fetchMonths = async () => {
      setMonthListLoading(true);
      try {
        const response = await ApiService.get(`/v1/metrics/getPastNMonths/${monthCount}`);
        const months = response.data?.months || [];
        setMonthList(months);
        // Default: show current month in filter, but tables show current + previous month if available
        const initialTableMonths = months.length > 1 ? [months[0], months[1]] : months.length > 0 ? [months[0]] : [];
        setSelectedMonths(initialTableMonths);
        setTableMonths(initialTableMonths);
      } catch (error) {
        setMonthList([]);
        setSelectedMonths([]);
        setTableMonths([]);
      } finally {
        setMonthListLoading(false);
      }
    };
    fetchMonths();
  }, [monthCount]);

  // Handle month filter change for tables
  const handleMonthFilterChange = (val) => {
    if (!monthList || monthList.length === 0) {
      setSelectedMonths([]);
      setTableMonths([]);
      return;
    }
    let monthsToShow = [];
    if (val === "last3") {
      monthsToShow = monthList.slice(1, 4); // Exclude current month
    } else if (val === "last6") {
      monthsToShow = monthList.slice(1, 7);
    } else if (val === "last12") {
      monthsToShow = monthList.slice(1, 13);
    } else if (val === monthList[0]) {
      monthsToShow = [monthList[0]];
    } else {
      monthsToShow = [val];
    }
    setSelectedMonths(monthsToShow);
    setTableMonths(monthsToShow);
  };

  // Prepare chart data from forecastPerformance
  let chartLabels = [];
  let chartSeries = [];

  if (
    forecastPerformance &&
    forecastPerformance.monthData &&
    Array.isArray(forecastPerformance.erroCount)
  ) {
    chartLabels = forecastPerformance.monthData.map((m) => {
      const parts = m.split("-");
      const monthNum = parts[1];
      const monthIdx = parseInt(monthNum, 10) - 1;
      return monthLabels[monthIdx] || m;
    });

    // AG Charts expects series as array of objects with xKey/yKey or category/value
    if (
      agg === "customer_id,location_id" ||
      agg === "bottler" ||
      agg === "customer" ||
      agg === "location"
    ) {
      // Stacked bar chart for each entity
      chartSeries = forecastPerformance.erroCount
        .filter(row => Array.isArray(row.successPercentage))
        .map((row, idx) => {
          // Build data for each entity for all months
          return chartLabels.map((label, mIdx) => ({
            entity:
              agg === "bottler"
                ? (row.bottlerCode ? row.bottlerCode : "") + (row.bottlerName ? " - " + row.bottlerName : "")
                : agg === "customer"
                ? (row.customerCode ? row.customerCode : "") + (row.customerName ? " - " + row.customerName : "")
                : agg === "location"
                ? (row.locationCode ? row.locationCode : "") + (row.locationName ? " - " + row.locationName : "")
                : (row.customerCode ? row.customerCode : "") +
                  (row.customerName ? " - " + row.customerName : "") +
                  (row.locationCode ? " - " + row.locationCode : "") +
                  (row.locationName ? " - " + row.locationName : ""),
            month: label,
            success: Math.round(row.successPercentage[mIdx] * 100) / 100,
            error: Math.round((100 - row.successPercentage[mIdx]) * 100) / 100,
          }));
        });
      // Flatten for AG Charts
      chartSeries = chartSeries.flat();
    } else {
      // Default: show average line for all
      const monthCount = forecastPerformance.monthData.length;
      const avgSuccess = Array(monthCount).fill(0);
      forecastPerformance.erroCount.forEach((row) => {
        if (Array.isArray(row.successPercentage)) {
          row.successPercentage.forEach((val, idx) => {
            avgSuccess[idx] += val;
          });
        }
      });
      for (let i = 0; i < monthCount; i++) {
        avgSuccess[i] = avgSuccess[i] / forecastPerformance.erroCount.length;
      }
      chartSeries = chartLabels.map((label, idx) => ({
        month: label,
        success: Math.round(avgSuccess[idx] * 100) / 100,
        error: Math.round((100 - avgSuccess[idx]) * 100) / 100,
      }));
    }
  }

 
  // Update agg if aggregate prop changes (when switching tabs)
  React.useEffect(() => {
    if (aggregate && aggregate !== agg) {
      setAgg(aggregate);
    }
  }, [aggregate]);

  // Dynamically generate columns for each month in the response (for Ag Grid)
  let dynamicColumns = [];
  if (agg === 'customer_id,location_id') {
    dynamicColumns.push({ headerName: 'Forecast Partner', field: 'customerLabel', minWidth: 220, maxWidth: 450, flex: 1, resizable: true });
    dynamicColumns.push({ headerName: 'Plant', field: 'locationLabel', minWidth: 220, maxWidth: 450, flex: 1, resizable: true });
  } else {
    let firstColHeader = agg === 'location' ? 'Plant' : agg === 'bottler' ? 'Group' : agg === 'customer' ? 'Forecast Partner' : 'Customer';
    dynamicColumns.push({ headerName: firstColHeader, field: 'mainLabel', minWidth: 220, maxWidth: 450, flex: 1, resizable: true });
  }
  if (forecastPerformance && forecastPerformance.monthData && forecastPerformance.monthData.length > 0) {
    forecastPerformance.monthData.forEach((month, mIdx) => {
      dynamicColumns.push(
        { headerName: `${month} Success %`, field: `success_${mIdx}`, minWidth: 160, maxWidth: 200, flex: 1, type: 'number', cellStyle: { textAlign: 'center' }, resizable: true },
        { headerName: `${month} Error %`, field: `error_${mIdx}`, minWidth: 160, maxWidth: 200, flex: 1, type: 'number', cellStyle: { textAlign: 'center' }, resizable: true },
        {
          headerName: `${month} On Time`,
          field: `onTime_${mIdx}`,
          minWidth: 120,
          maxWidth: 160,
          flex: 1,
          cellStyle: { textAlign: 'center' },
          resizable: true,
          cellRenderer: function (params) {
            // Use React for icon rendering
            if (params.value === "Yes") {
              return React.createElement(CheckCircleIcon, { style: { color: 'green', fontSize: 22 } });
            }
            if (params.value === "No") {
              return React.createElement(CancelIcon, { style: { color: 'red', fontSize: 22 } });
            }
            return params.value || '';
          }
        },
        {
          headerName: `${month} Variance`,
          field: `variance_${mIdx}`,
          minWidth: 120,
          maxWidth: 160,
          flex: 1,
          cellStyle: { textAlign: 'center' },
          resizable: true,
          cellRenderer: function (params) {
            if (params.value === "Yes") {
              return React.createElement(CheckCircleIcon, { style: { color: 'green', fontSize: 22 } });
            }
            if (params.value === "No") {
              return React.createElement(CancelIcon, { style: { color: 'red', fontSize: 22 } });
            }
            return params.value || '';
          }
        }
      );
    });
  }



 
  // Table data state
  const [tableRows, setTableRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);

  // Fetch table data from API
  useEffect(() => {
    const fetchTableData = async () => {
      if (finalTreeData.length > 0 && selectedMonths.length > 0) {
        setTableLoading(true);
        try {
          const payload = {
            locationCustomerDTOList: finalTreeData,
            month: selectedMonths[0], // Use first selected month for table
          };
          const response = await ApiService.post("/v1/metrics/fetch-submission-data", payload);
          setTableRows(response.data || []);
        } catch (error) {
          setTableRows([]);
        } finally {
          setTableLoading(false);
        }
      } else {
        setTableRows([]);
        setTableLoading(false);
      }
    };
    fetchTableData();
  }, [finalTreeData, selectedMonths]);

  // Status filter for table
  const [statusFilter, setStatusFilter] = useState(null);
  const filteredTableRows = statusFilter
    ? tableRows.filter(row => row.status === statusFilter)
    : tableRows;

  

  // AG Charts line chart options
 

  return (
    <div className="container">
      {/* 1. Heading */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" align="left" gutterBottom>
          Historical Submission performance
        </Typography>
        <InfoPopover margin="0 0 3px 0" />
      </Box> */}


       {/* 2. Graph */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <PerformanceGraph/>
      </Box>

      {/* 3. Filters */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        {/* Left: Month filter and term pill */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Month single-select filter */}
          <FormControl
            size="small"
            sx={{
              border: "1px solid black",
              borderRadius: 5,
              px: 0.25,
              minWidth: 160,
              height: 28,
              backgroundColor: "#fff"
            }}
          >
            {monthListLoading ? (
              <Skeleton variant="rectangular" width={160} height={28} />
            ) : (
              <Select
                value={
                  // Show filter value as string for correct highlighting
                  selectedMonths.length === 1 && selectedMonths[0] === monthList[0]
                    ? monthList[0]
                    : selectedMonths.length === 3 && selectedMonths[0] === monthList[1]
                    ? "last3"
                    : selectedMonths.length === 6 && selectedMonths[0] === monthList[1]
                    ? "last6"
                    : selectedMonths.length === 12 && selectedMonths[0] === monthList[1]
                    ? "last12"
                    : selectedMonths[0] || ""
                }
                  MenuProps={{
                    disableScrollLock: true, // Disable scroll lock for the Select's dropdown menu
                  }}
                onChange={e => handleMonthFilterChange(e.target.value)}
                sx={{
                  fontSize: "0.8rem",
                  padding: "6px 8px",
                  ".MuiOutlinedInput-input": {
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value={monthList[0] || ""}>Current Month</MenuItem>
                <MenuItem value="last3">Past 3 Months</MenuItem>
                <MenuItem value="last6">Past 6 Months</MenuItem>
                <MenuItem value="last12">Past Year</MenuItem>
              </Select>
            )}
          </FormControl>
          {/* Pill filter for Short/Long Term */}
          <PillContainer>
            <PillHighlight selected={term} />
            <PillLabel
              active={term === '1'}
              onClick={() => handleTermChange('1')}
              style={{ fontWeight: term === '1' ? 500 : 400 }}
            >
              Short Term
            </PillLabel>
            <PillLabel
              active={term === '2'}
              onClick={() => handleTermChange('2')}
              style={{ fontWeight: term === '2' ? 500 : 400 }}
            >
              Long Term
            </PillLabel>
          </PillContainer>
        </Box>
        {/* Right: Aggregate pill selector (Plant/Bottler) */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AggregatePillContainer>
            <AggregatePillHighlight selected={agg} />
            <AggregatePillLabel
              active={agg === 'plant'}
              onClick={() => setAgg('plant')}
            >
              Plant
            </AggregatePillLabel>
            <AggregatePillLabel
              active={agg === 'bottler'}
              onClick={() => setAgg('bottler')}
            >
              Bottler
            </AggregatePillLabel>
          </AggregatePillContainer>
        </Box>
      </Box>

      {/* 4. Table(s) */}
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minHeight: 300,
                "& .MuiDataGrid-root": { background: '#fff' },
                "& .MuiDataGrid-columnHeaders": { fontWeight: 700, fontSize: 16 },
                "& .MuiDataGrid-cell": { fontSize: 15 },
              }}
            >
              {forecastPerformanceLoading ? (
                <Skeleton variant="rectangular" width="100%" height={300} animation="wave" />
              ) : (
                agg === 'plant' ? (
                  // Two tables for Plant, side by side
                  <Stack direction="column" spacing={3}>
                    <PlantTable
                      forecastPerformance={forecastPerformance}
                      selectedMonths={tableMonths}
                      fiscalMonths={tableMonths}
                      forecastTerm={term}
                    />
                    <BottlerTable
                      fiscalMonths={tableMonths}
                      forecastTerm={term}
                    />
                  </Stack>
                ) : (
                  // One table for Bottler view: show Customer Country Table
                  <CustomerCountryTable
                    fiscalMonths={tableMonths}
                    forecastTerm={term}
                  />
                )
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default DashboardCharts;